/**
 * PRD-042 Phase 5 P2: Price Contribution Summary
 *
 * Shows users a summary of their price contributions after completing
 * a Group Share transaction. Encourages continued data contribution.
 *
 * @component
 * @example
 * <PriceContributionSummary
 *   pricesSubmitted={5}
 *   commodityNames={['Gold', 'Titanium', 'Medical Supplies']}
 * />
 */

import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import './PriceContributionSummary.css';

/**
 * Price Contribution Summary - Shows contribution impact after Group Share
 *
 * @param {Object} props - Component props
 * @param {number} props.pricesSubmitted - Number of prices submitted
 * @param {string[]} [props.commodityNames=[]] - Names of commodities with prices
 * @param {boolean} [props.compact=false] - Compact display mode
 * @param {string} [props.className=''] - Additional CSS classes
 * @param {Function} [props.onViewCharts] - Callback to navigate to charts
 */
const PriceContributionSummary = memo(function PriceContributionSummary({
  pricesSubmitted = 0,
  commodityNames = [],
  compact = false,
  className = '',
  onViewCharts
}) {
  const navigate = useNavigate();

  const handleViewCharts = () => {
    if (onViewCharts) {
      onViewCharts();
    } else {
      navigate('/charts');
    }
  };

  // Don't show if no prices were submitted
  if (pricesSubmitted === 0) {
    return null;
  }

  // Build commodity list display
  const displayCommodities = commodityNames.slice(0, 3);
  const remainingCount = commodityNames.length - 3;

  if (compact) {
    return (
      <div className={`price-contribution-summary compact ${className}`}>
        <span className="contribution-icon">📊</span>
        <span className="contribution-text">
          {pricesSubmitted} price{pricesSubmitted !== 1 ? 's' : ''} shared with community
        </span>
      </div>
    );
  }

  return (
    <div className={`price-contribution-summary ${className}`}>
      <div className="contribution-header">
        <span className="contribution-icon-large">🎉</span>
        <div className="contribution-title">
          <span className="title-text">Thanks for Contributing!</span>
          <span className="title-count">
            {pricesSubmitted} price{pricesSubmitted !== 1 ? 's' : ''} shared
          </span>
        </div>
      </div>

      <div className="contribution-details">
        <p className="contribution-message">
          Your sale prices help other traders make better decisions.
          The community thanks you!
        </p>

        {displayCommodities.length > 0 && (
          <div className="contribution-commodities">
            <span className="commodities-label">Commodities updated:</span>
            <div className="commodities-list">
              {displayCommodities.map((name, idx) => (
                <span key={idx} className="commodity-tag">{name}</span>
              ))}
              {remainingCount > 0 && (
                <span className="commodity-more">+{remainingCount} more</span>
              )}
            </div>
          </div>
        )}
      </div>

      <button
        className="contribution-cta"
        onClick={handleViewCharts}
        type="button"
      >
        View Community Charts →
      </button>
    </div>
  );
});

// PropTypes validation
PriceContributionSummary.propTypes = {
  /** Number of prices submitted */
  pricesSubmitted: PropTypes.number,
  /** Names of commodities with prices */
  commodityNames: PropTypes.arrayOf(PropTypes.string),
  /** Compact display mode */
  compact: PropTypes.bool,
  /** Additional CSS classes */
  className: PropTypes.string,
  /** Callback to navigate to charts */
  onViewCharts: PropTypes.func
};

export default PriceContributionSummary;
